
import { useState, useEffect } from 'react';
import type { Article } from '../../../mocks/blog';

interface ArticleFormProps {
  article?: Article | null;
  onSave: (article: Article) => void;
  onCancel: () => void;
}

const AVAILABLE_TAGS = ['AI 虛擬人', 'IP 建構', '商業應用', '短視頻', '品牌行銷', '動態特效', '設計趨勢', '3D 動畫', 'CGI', '品牌策略', 'AI 技術', '影視動畫', '視覺設計', '製作流程', '視覺資產', '產業趨勢'];

function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

export default function ArticleForm({ article, onSave, onCancel }: ArticleFormProps) {
  const isEdit = !!article;

  const [title, setTitle] = useState('');
  const [coverImage, setCoverImage] = useState('');
  const [summary, setSummary] = useState('');
  const [content, setContent] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [tags, setTags] = useState<string[]>([]);
  const [customTag, setCustomTag] = useState('');
  const [status, setStatus] = useState<'published' | 'draft'>('published');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    if (article) {
      setTitle(article.title);
      setCoverImage(article.coverImage);
      setSummary(article.summary);
      setContent(article.content);
      setDate(article.date);
      setTags(article.tags);
      setStatus(article.status);
    }
  }, [article]);

  const toggleTag = (tag: string) => {
    setTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const addCustomTag = () => {
    const t = customTag.trim();
    if (t && !tags.includes(t)) {
      setTags((prev) => [...prev, t]);
    }
    setCustomTag('');
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!title.trim()) e.title = '請填寫文章標題';
    if (!summary.trim()) e.summary = '請填寫文章摘要';
    if (!content.trim()) e.content = '請填寫文章內容';
    if (tags.length === 0) e.tags = '請至少選擇一個標籤';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    const saved: Article = {
      id: article?.id ?? generateId(),
      title: title.trim(),
      coverImage: coverImage.trim() || 'https://public.readdy.ai/ai/img_res/9b55d8debfdd4ff246ff4d34ad855e29.jpg',
      summary: summary.trim(),
      content: content.trim(),
      date,
      tags,
      status,
    };
    onSave(saved);
  };

  const inputClass = 'w-full px-4 py-3 bg-white/[0.04] border border-amber-500/15 rounded-sm text-white/80 placeholder-white/20 focus:outline-none focus:border-amber-400/50 transition-all duration-300 text-sm';
  const labelClass = 'block text-white/50 text-xs tracking-[0.25em] mb-2';

  return (
    <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-screen px-6 py-10">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <button
                onClick={onCancel}
                className="w-9 h-9 flex items-center justify-center border border-amber-500/20 rounded-sm text-white/40 hover:text-amber-400 hover:border-amber-400/40 transition-all duration-300 cursor-pointer"
              >
                <i className="ri-arrow-left-line text-sm" />
              </button>
              <div>
                <h2
                  className="text-lg font-bold text-white/90 tracking-wider"
                  style={{ fontFamily: "'Noto Serif TC', serif" }}
                >
                  {isEdit ? '編輯文章' : '新增文章'}
                </h2>
                <p className="text-white/25 text-xs tracking-[0.2em]" style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>
                  {isEdit ? 'EDIT ARTICLE' : 'NEW ARTICLE'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Preview toggle */}
              <button
                onClick={() => setPreviewMode((v) => !v)}
                className={`px-4 py-2 text-xs tracking-wider border rounded-sm transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  previewMode
                    ? 'bg-amber-500/15 border-amber-400/40 text-amber-400'
                    : 'bg-white/[0.03] border-white/10 text-white/40 hover:border-amber-500/30 hover:text-amber-400/70'
                }`}
                style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
              >
                <i className={`${previewMode ? 'ri-edit-line' : 'ri-eye-line'} mr-1.5`} />
                {previewMode ? '編輯模式' : '預覽內容'}
              </button>

              {/* Status toggle */}
              <button
                onClick={() => setStatus((s) => (s === 'published' ? 'draft' : 'published'))}
                className={`px-4 py-2 text-xs tracking-wider border rounded-sm transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  status === 'published'
                    ? 'bg-emerald-500/15 border-emerald-400/40 text-emerald-400'
                    : 'bg-white/[0.03] border-white/10 text-white/40 hover:border-amber-500/30'
                }`}
                style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
              >
                <i className={`${status === 'published' ? 'ri-checkbox-circle-line' : 'ri-draft-line'} mr-1.5`} />
                {status === 'published' ? '已發布' : '草稿'}
              </button>

              {/* Save */}
              <button
                onClick={handleSave}
                className="px-6 py-2 bg-gradient-to-r from-amber-500 to-yellow-400 rounded-sm text-black text-xs font-bold tracking-widest hover:shadow-lg hover:shadow-amber-500/40 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
                style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
              >
                <i className="ri-save-line mr-1.5" />
                儲存文章
              </button>
            </div>
          </div>

          {previewMode ? (
            /* Preview */
            <div className="bg-white/[0.02] border border-amber-500/10 rounded-sm p-10">
              <div className="max-w-2xl mx-auto">
                {coverImage && (
                  <div className="w-full h-64 overflow-hidden rounded-sm mb-8">
                    <img src={coverImage} alt={title} className="w-full h-full object-cover object-top" />
                  </div>
                )}
                <div className="flex flex-wrap gap-2 mb-4">
                  {tags.map((t) => (
                    <span key={t} className="px-2.5 py-0.5 bg-amber-500/15 border border-amber-500/25 text-amber-400 text-xs rounded-sm" style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>{t}</span>
                  ))}
                </div>
                <h1 className="text-3xl font-black text-white/90 mb-3 leading-tight" style={{ fontFamily: "'Noto Serif TC', serif" }}>{title || '（未填寫標題）'}</h1>
                <p className="text-white/40 text-sm mb-2" style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>{date}</p>
                <div className="border-l-2 border-amber-500/40 pl-4 mb-8">
                  <p className="text-white/55 text-sm italic leading-relaxed" style={{ fontFamily: "'Noto Serif TC', serif" }}>{summary || '（未填寫摘要）'}</p>
                </div>
                <div
                  className="article-body"
                  dangerouslySetInnerHTML={{ __html: content || '<p style="color:rgba(255,255,255,0.3)">（未填寫內容）</p>' }}
                />
              </div>
            </div>
          ) : (
            /* Edit form */
            <div className="grid grid-cols-3 gap-6">
              {/* Left: main fields */}
              <div className="col-span-2 space-y-6">
                {/* Title */}
                <div className="bg-white/[0.02] border border-amber-500/10 rounded-sm p-6">
                  <label className={labelClass} style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>文章標題 *</label>
                  <input
                    type="text"
                    placeholder="輸入吸引人的標題..."
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className={inputClass}
                    style={{ fontFamily: "'Noto Serif TC', serif", fontSize: '1.1rem' }}
                  />
                  {errors.title && <p className="text-red-400/70 text-xs mt-2" style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>{errors.title}</p>}
                </div>

                {/* Summary */}
                <div className="bg-white/[0.02] border border-amber-500/10 rounded-sm p-6">
                  <label className={labelClass} style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>文章摘要 *</label>
                  <textarea
                    placeholder="簡短描述文章重點，顯示在文章列表卡片上..."
                    value={summary}
                    onChange={(e) => setSummary(e.target.value)}
                    rows={3}
                    maxLength={300}
                    className={`${inputClass} resize-none`}
                    style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
                  />
                  <div className="flex justify-between mt-1">
                    {errors.summary
                      ? <p className="text-red-400/70 text-xs" style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>{errors.summary}</p>
                      : <span />
                    }
                    <span className="text-white/20 text-xs" style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>{summary.length}/300</span>
                  </div>
                </div>

                {/* Content */}
                <div className="bg-white/[0.02] border border-amber-500/10 rounded-sm p-6">
                  <div className="flex items-center justify-between mb-2">
                    <label className={labelClass} style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>文章內容（支援 HTML）*</label>
                    <span className="text-white/20 text-xs" style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>{content.length} 字</span>
                  </div>

                  {/* Quick format toolbar */}
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {[
                      { label: 'H2', insert: '<h2></h2>' },
                      { label: 'H3', insert: '<h3></h3>' },
                      { label: 'P', insert: '<p></p>' },
                      { label: 'UL', insert: '<ul>\n  <li></li>\n</ul>' },
                      { label: 'OL', insert: '<ol>\n  <li></li>\n</ol>' },
                      { label: 'B', insert: '<strong></strong>' },
                      { label: 'I', insert: '<em></em>' },
                      { label: 'Quote', insert: '<blockquote></blockquote>' },
                      { label: 'Code', insert: '<code></code>' },
                    ].map(({ label, insert }) => (
                      <button
                        key={label}
                        type="button"
                        onClick={() => setContent((c) => c + insert)}
                        className="px-2.5 py-1 bg-white/[0.04] border border-amber-500/10 text-white/40 text-xs rounded-sm hover:border-amber-400/30 hover:text-amber-400/70 transition-all duration-200 cursor-pointer whitespace-nowrap"
                        style={{ fontFamily: 'monospace' }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  <textarea
                    placeholder="在此輸入文章內容，支援 HTML 標籤格式..."
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    rows={18}
                    className={`${inputClass} resize-y font-mono text-xs leading-relaxed`}
                  />
                  {errors.content && <p className="text-red-400/70 text-xs mt-2" style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>{errors.content}</p>}
                </div>
              </div>

              {/* Right: meta */}
              <div className="space-y-6">
                {/* Cover image */}
                <div className="bg-white/[0.02] border border-amber-500/10 rounded-sm p-6">
                  <label className={labelClass} style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>封面圖片 URL</label>
                  {coverImage && (
                    <div className="w-full h-36 overflow-hidden rounded-sm mb-3">
                      <img
                        src={coverImage}
                        alt="封面預覽"
                        className="w-full h-full object-cover object-top"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    </div>
                  )}
                  <input
                    type="text"
                    placeholder="貼上圖片網址..."
                    value={coverImage}
                    onChange={(e) => setCoverImage(e.target.value)}
                    className={inputClass}
                    style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
                  />
                  <p className="text-white/20 text-xs mt-2 leading-relaxed" style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>
                    留空將使用預設封面圖
                  </p>
                </div>

                {/* Date */}
                <div className="bg-white/[0.02] border border-amber-500/10 rounded-sm p-6">
                  <label className={labelClass} style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>發布日期</label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className={`${inputClass} [color-scheme:dark]`}
                    style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
                  />
                </div>

                {/* Tags */}
                <div className="bg-white/[0.02] border border-amber-500/10 rounded-sm p-6">
                  <label className={labelClass} style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>文章標籤 *</label>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {AVAILABLE_TAGS.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => toggleTag(tag)}
                        className={`px-2.5 py-1 text-xs tracking-wider rounded-sm border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                          tags.includes(tag)
                            ? 'bg-amber-500/20 border-amber-400/50 text-amber-400'
                            : 'bg-white/[0.03] border-white/10 text-white/35 hover:border-amber-500/25 hover:text-amber-400/60'
                        }`}
                        style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>

                  {/* Custom tag */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="自訂標籤..."
                      value={customTag}
                      onChange={(e) => setCustomTag(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomTag(); } }}
                      className="flex-1 px-3 py-2 bg-white/[0.04] border border-amber-500/15 rounded-sm text-white/70 placeholder-white/20 focus:outline-none focus:border-amber-400/40 text-xs transition-all duration-300"
                      style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
                    />
                    <button
                      type="button"
                      onClick={addCustomTag}
                      className="px-3 py-2 bg-amber-500/15 border border-amber-500/25 text-amber-400 text-xs rounded-sm hover:bg-amber-500/25 transition-all duration-200 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-add-line" />
                    </button>
                  </div>

                  {/* Selected tags */}
                  {tags.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {tags.map((t) => (
                        <span
                          key={t}
                          className="flex items-center gap-1 px-2 py-0.5 bg-amber-500/15 border border-amber-500/25 text-amber-400 text-xs rounded-sm"
                          style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
                        >
                          {t}
                          <button
                            type="button"
                            onClick={() => toggleTag(t)}
                            className="text-amber-400/50 hover:text-amber-400 transition-colors cursor-pointer"
                          >
                            <i className="ri-close-line text-xs" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {errors.tags && <p className="text-red-400/70 text-xs mt-2" style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>{errors.tags}</p>}
                </div>

                {/* Status card */}
                <div className="bg-white/[0.02] border border-amber-500/10 rounded-sm p-6">
                  <label className={labelClass} style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>發布狀態</label>
                  <div className="flex gap-3">
                    {(['published', 'draft'] as const).map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setStatus(s)}
                        className={`flex-1 py-2.5 text-xs tracking-wider border rounded-sm transition-all duration-300 cursor-pointer whitespace-nowrap ${
                          status === s
                            ? s === 'published'
                              ? 'bg-emerald-500/15 border-emerald-400/40 text-emerald-400'
                              : 'bg-amber-500/15 border-amber-400/40 text-amber-400'
                            : 'bg-white/[0.03] border-white/10 text-white/30 hover:border-white/20'
                        }`}
                        style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
                      >
                        <i className={`${s === 'published' ? 'ri-checkbox-circle-line' : 'ri-draft-line'} mr-1.5`} />
                        {s === 'published' ? '發布' : '草稿'}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
